import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import Icon from "../../../../components/general/icon";
import TextAreaField from "../../../../components/general/form/textAreaField";
import { general } from "../../../../../store/action";
import {
  TaminReferralType,
  TDraftDescriptionField,
  TVisitFields,
} from "../../../../../types/visit";
import AlertBox from "../../../../components/general/alert";
import PrescriptionMedicationItem from "./prescriptionMedicationItem";
import PrescriptionParaclinicalItem from "./prescriptionParaclinicalItem";
import {
  ISSUER_TYPE,
  VISIT_MODE,
} from "../../../../../constants/constant/enum";
import {
  TCheckInsuranceFields,
  TIhioDataAndPrescriptionMedicationItemFields,
  TIhioDataAndPrescriptionParaclinicalItemFields,
  TPrescriptionFields,
} from "../../../../../types/prescription";
import { useGetPrescription } from "../../../../../hook/request/visit/prescription";
import { useLocation } from "react-router-dom";
import TaminReferralSummary from "./taminReferralSummary";
import { v4 as uuidv4 } from "uuid";
import TextAreaFieldV2T from "../../../../components/general/form/textAreaFiledV2T";
import { SakhadComplaintCodeDropdownMenu } from "./sakhadComplaintCodeDropdownMenu";

import { isToday } from "../../../../../utils/date";
type PrescriptionProps = {
  visitData?: TVisitFields;
  taminReferralData?: TaminReferralType | null;
  checkInsurance?: TCheckInsuranceFields;
  prescriptionData?: TPrescriptionFields;
  medicationItemsCheckCodes?: string[];
  paraclinicalItemsCheckCodes?: string[];
  isRequesting?: boolean;
  submitEndVisitCheck?: boolean;
  handleShowModal: any;
  updatePrescriptionFields: any;
  handlePrescriptionModalSubmit: any;
  removeReferralData: any;
  willDoItMyselfCounter: React.MutableRefObject<number>;
};
// Form type
// type FormFields = TDraftDescriptionField & { voiceToText: string };

const Prescription = ({
  visitData,
  taminReferralData,
  checkInsurance,
  updatePrescriptionFields,
  prescriptionData,
  medicationItemsCheckCodes,
  paraclinicalItemsCheckCodes,
  handlePrescriptionModalSubmit,
  removeReferralData,
  isRequesting,
  willDoItMyselfCounter,
}: PrescriptionProps) => {
  //<editor-fold desc="States">
  const location = useLocation();
  const mode = location?.state?.mode || VISIT_MODE.SHOW.value;
  const [fieldValue, setFieldValue] = useState<TDraftDescriptionField>({
    draftDescription: "",
  });
  const {
    control,
    reset,
    formState: { errors },
  } = useForm<TDraftDescriptionField>({
    defaultValues: useMemo(() => fieldValue, [fieldValue]),
  });
  const [insurance, setInsurance] = useState(0);
  const [
    medicationPrescriptionFieldValue,
    setMedicationPrescriptionFieldValue,
  ] = useState<TIhioDataAndPrescriptionMedicationItemFields[]>([]);
  const [
    paraclinicalPrescriptionFieldValue,
    setParaclinicalPrescriptionFieldValue,
  ] = useState<TIhioDataAndPrescriptionParaclinicalItemFields[]>([]);

  // این برای ذخیره اندیس دلیل مراجعه و بیماری بیماران ساخد است complaintCode
  const [sakhadComplaintCode, setSakhadComplaintCode] = useState(0);
  //</editor-fold>

  const [isVisitForToday, setIsVisitForToday] = useState<boolean>(false);

  // React Hook Form with renamed variables to avoid conflicts
  // const {
  //   control: formControl,
  //   reset: formReset,
  //   setValue,
  //   formState: { errors: formErrors },
  // } = useForm<FormFields>({
  //   defaultValues: useMemo(
  //     () => ({
  //       draftDescription: fieldValue.draftDescription,
  //       voiceToText: "",
  //     }),
  //     [fieldValue]
  //   ),
  // });

  //<editor-fold desc="Services">
  const getPrescriptionRequest = useGetPrescription();
  //</editor-fold>

  //<editor-fold desc="UseEffects">
  useEffect(() => {
    visitData?.id &&
      getPrescriptionRequest.mutate({ visit: { id: visitData.id } });
  }, [visitData]);

  useEffect(() => {
    setFieldValue({ draftDescription: prescriptionData?.draftDescription });
  }, [prescriptionData]);

  useEffect(() => {
    reset(fieldValue);
  }, [fieldValue]);

  useEffect(() => {
    // console.log("DOSAGE01", insurance);
    if (getPrescriptionRequest?.data?.data?.result) {
      const {
        id,
        insurerStatus,
        medicationItems,
        paraclinicalItems,
        draftDescription,
        sakhadComplaintCode,
      } = getPrescriptionRequest.data.data.result;
      handlePrescriptionModalSubmit(id, insurerStatus, draftDescription);
      setSakhadComplaintCode(sakhadComplaintCode);

      // console.log("SAKHAD_001", getPrescriptionRequest.data.data.result);
      const bulkGroups: any[] = [];
      const individualItems: any[] = [];

      const extractedData: TIhioDataAndPrescriptionMedicationItemFields[] =
        medicationItems.map((medication: any) => {
          if (Object.keys(medication).length === 0) {
            // Return an empty object if the prescription object is empty
            return {};
          }
          // Return the mapped object if the prescription object is not empty

          const { ihioCheck, bulkId, ...restOfMedication } = medication;
          const UUID = uuidv4();
          const item = {
            ...restOfMedication,
            UUID,
            checkCode: ihioCheck,
            bulkId,
          };

          if (bulkId && bulkId !== 0) {
            if (!bulkGroups[bulkId]) {
              bulkGroups[bulkId] = [];
            }
            bulkGroups[bulkId].push(item);
          } else {
            individualItems.push({
              item: medication.item,
              dosage: medication.dosage.id,
              dosageName: medication.dosage.faName,
              frequency: medication.frequency.id,
              frequencyName: medication.frequency.name,
              itemCount: medication.itemCount,
              checkCode: medication.ihioCheck,
              drugDescription: medication.drugDescription,
              activeDate: medication.activeDate,
              brand: medication?.item?.id,
              brandName: medication?.item?.brand,
              bulkId: medication.bulkId,
              sakhadPrescribeServiceUUID: medication.sakhadPrescribeServiceUUID,
            });
          }
        });

      const groupedItems = bulkGroups.filter(Boolean).map((group) => ({
        UUID: uuidv4(), // Unique ID for the group
        bulkId: group[0].bulkId,
        bulkItems: group,
      }));

      const extractedParaData: TIhioDataAndPrescriptionParaclinicalItemFields[] =
        paraclinicalItems.map((paraclinical: any) => {
          // Return the mapped object if the paraclinical object is not empty
          return {
            service: paraclinical.service,
            emergency: paraclinical.emergency,
            itemCount: paraclinical.itemCount,
            checkCode: paraclinical.ihioCheck,
            note: paraclinical.note,
            activeDate: paraclinical.activeDate,
            sakhadPrescribeServiceUUID: paraclinical.sakhadPrescribeServiceUUID,
          };
        });

      const event = new CustomEvent("lastBulkGroupIdForPrescription", {
        // کال کردن یک ایونت برای آپدیت آیدی بالک در مودال بالک
        detail: { bulkId: groupedItems[groupedItems?.length - 1]?.bulkId },
      });
      window.dispatchEvent(event);

      setMedicationPrescriptionFieldValue([
        ...individualItems,
        ...groupedItems,
      ]);
      setParaclinicalPrescriptionFieldValue(extractedParaData);
      // console.log("PRES_02", extractedParaData);
    }
  }, [getPrescriptionRequest.data]);

  /*
    مشخص کردن نوع بیمه بیمار جهت ثبت نسخه
    و دریافت اطلاعات دارو ها و پاراکلینیک بر اساس آن.
    @Dependency: string
  */
  useEffect(() => {
    const insuranceId = checkInsurance?.issuerType?.id;
    // console.log("DOSAGE_prescription", insuranceId);

    if (insuranceId === ISSUER_TYPE.ESATA.value) {
      setInsurance(ISSUER_TYPE.ESATA.value);
    } else if (insuranceId === ISSUER_TYPE.TAMIN.value) {
      setInsurance(ISSUER_TYPE.TAMIN.value);
    } else if (
      insuranceId === ISSUER_TYPE.M.value
      /*|| insuranceId === ISSUER_TYPE.ESATA.value*/
    ) {
      setInsurance(ISSUER_TYPE.M.value);
    } else if (insuranceId === ISSUER_TYPE.B.value) {
      setInsurance(ISSUER_TYPE.B.value);
    } else {
      setInsurance(ISSUER_TYPE.IHIO.value);
    }
  }, [checkInsurance]);
  //</editor-fold>

  /*
    آپدیت داده ها در پرنت جهت ارسال به مودال ثبت نسخه.
    @param: item
    @param: name
  */
  const updateData = (item: any, name: string) => {
    updatePrescriptionFields(item, name);
  };

  /*
    آپدیت داده توضیحات تکمیلی.
    @param: e
  */
  const handleFieldChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e?.target;
    updateData(value, name);
  };

  useEffect(() => {
    sakhadComplaintCodeChange(sakhadComplaintCode);
  }, [sakhadComplaintCode]);

  // تابع آپدیت اندیس کامپلینت کد بیماران در ساخد
  const sakhadComplaintCodeChange = (index: number) => {
    setSakhadComplaintCode(index);
    updateData(index, "sakhadComplaintCode");
  };

  // In useForm

  return (
    <>
      <div className="card-box shadow-none rounded-0">
        {prescriptionData?.insurerStatus?.errorMessage && (
          <AlertBox
            type="danger"
            icon="info-circle"
            iconClass="fs-2"
            className="border-red border-2 mb-3"
          >
            <div className="d-flex align-items-center w-100 justify-content-between">
              <span>{prescriptionData?.insurerStatus?.errorMessage}</span>
              <div className="d-flex justify-content-md-end mb-3 m-md-0 ">
                <span
                  // onClick={() => handleShowModal('activeScheduleModal', id)}
                  className="btn btn-dark text-nowrap rounded-3 d-flex align-items-center px-3"
                >
                  ارسال مجدد به بیمه
                </span>
              </div>
            </div>
          </AlertBox>
        )}
        {(checkInsurance?.snackMessage?.warning?.length ?? 0) > 0 && (
          <AlertBox
            type="warning"
            icon="info-circle"
            iconClass="fs-2"
            text={checkInsurance?.snackMessage?.warning}
            className="border-orange border-2 mb-3"
            children=""
          />
        )}
        {taminReferralData && (
          <TaminReferralSummary
            taminReferralData={taminReferralData}
            updateData={updateData}
            removeReferralData={removeReferralData}
          />
        )}
        {checkInsurance?.issuerType?.id === ISSUER_TYPE.ESATA.value && (
          <div className="d-flex align-items-center align-content-center ms-auto mt-2 mb-4 border rounded-2 p-2">
            <span className="d-flex font-22">
              <Icon name="hospital" />
            </span>
            <span className="m-2 my-4">علت مراجعه بیمار:</span>
            <SakhadComplaintCodeDropdownMenu
              index={sakhadComplaintCode}
              updateData={sakhadComplaintCodeChange}
              disabled={mode === VISIT_MODE.SHOW.value}
            />
            {/* <div className="border border-black"></div> */}
          </div>
        )}
        <PrescriptionMedicationItem
          visitData={visitData}
          insurance={checkInsurance?.issuerType?.id} //{insurance}
          updateData={updateData}
          medicationPrescriptionFieldValues={medicationPrescriptionFieldValue}
          paraclinicalItemsCheckCodes={paraclinicalItemsCheckCodes}
          isRequesting={getPrescriptionRequest.isPending}
        />
        <PrescriptionParaclinicalItem
          visitData={visitData}
          insurance={checkInsurance?.issuerType?.id} //{insurance}
          updateData={updateData}
          paraclinicalPrescriptionFieldValues={
            paraclinicalPrescriptionFieldValue
          }
          medicationItemsCheckCodes={medicationItemsCheckCodes}
          isRequesting={getPrescriptionRequest.isPending}
          willDoItMyselfCounter={willDoItMyselfCounter}
        />
        <h4 className="d-flex font-14 mb-4 mt-4 pb-2">
          <div className="align-content-center ms-auto">
            <span>
              <Icon name="message-add" />
            </span>
            <span className="me-2">ثبت توضیحات تکمیلی</span>
          </div>
        </h4>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="d-flex">
            <div className="col-12">
              <div className="form-group mb-4">
                <TextAreaField
                  name="draftDescription"
                  placeholder="‌توضیحات"
                  control={control}
                  error={errors}
                  onChange={handleFieldChange}
                  disabled={mode === VISIT_MODE.SHOW.value}
                />
              </div>
            </div>
          </div>
          {/* <div className="d-flex"> */}
          {/* <div className="col-12">
              <TextAreaFieldV2T
                name="voiceToText"
                control={formControl}
                setValue={setValue}
                error={formErrors}
                disabled={mode === VISIT_MODE.SHOW.value}
                onChange={(value) => updateData(value, "voiceToText")}
              />
            </div> */}
          {/* </div> */}
        </form>
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  handleShowModal: (data: any, body: any) =>
    dispatch(general.handleShowModal(data, body)),
});

export default connect(null, mapDispatchToProps)(Prescription);
